import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useRef, useState, type FormEvent } from 'react';
import { api, ApiError, fieldIssues } from '../api';
import { can } from '../auth/me';
import { useMe } from '../auth/useMe';
import { parsePln, plnToInput } from '../catalog/money';
import { todayInWarsaw } from '../events/calendar';
import { formatDayText } from '../events/fields';
import { ConflictNotice } from '../ui/ConflictNotice';
import { DialogLayer } from '../ui/Dialog';
import { trapTab } from '../ui/focus';
import { QuickPicker, type PickerOption } from '../ui/QuickPicker';
import { sameValues, useOpening, useUnsavedGuard } from '../ui/unsaved';
import { FORM_NOTE_LABEL, FORMS, PAYMENTS_KEY, type Form, type Receivable } from './types';

type Ref = { id: string; name: string };
type Option = PickerOption & { id: string };

async function searchContractors(q: string, signal: AbortSignal): Promise<Option[]> {
  const res = await api<{ contractors: Ref[] }>(`/api/contractors?q=${encodeURIComponent(q)}`, { signal });
  return res.contractors.slice(0, 20).map((c) => ({ key: c.id, id: c.id, name: c.name, details: [] }));
}
async function searchEvents(q: string, signal: AbortSignal): Promise<Option[]> {
  const res = await api<{ events: { id: string; name?: string; startsAt?: string }[] }>(`/api/events?q=${encodeURIComponent(q)}`, { signal });
  return res.events.slice(0, 20).map((e) => ({ key: e.id, id: e.id, name: e.name ?? 'Wydarzenie', details: e.startsAt ? [formatDayText(todayInWarsaw(new Date(e.startsAt)))] : [] }));
}

/**
 * „+ Należność” (owner 2026-11-03 23:33) and „Zmień” of one: contractor (picked like in „Finanse → Barter”),
 * description, gross amount, due date and form; optionally the event it concerns. A change sends only what changed,
 * with the version (409: someone changed it meanwhile).
 */
export function ManualReceivableDialog({ receivable, onClose }: { receivable?: Receivable; onClose: () => void }) {
  const { me } = useMe();
  const queryClient = useQueryClient();
  const [who, setWho] = useState<Ref | null>(receivable?.contractorId ? { id: receivable.contractorId, name: receivable.contractorName } : null);
  const [event, setEvent] = useState<Ref | null>(receivable?.eventId ? { id: receivable.eventId, name: receivable.eventName } : null);
  const [label, setLabel] = useState(receivable?.label ?? '');
  const [amount, setAmount] = useState(receivable?.amountGrosze !== undefined ? plnToInput(receivable.amountGrosze) : '');
  const [dueDate, setDueDate] = useState(receivable?.dueDate ?? '');
  const [form, setForm] = useState<Form | ''>(receivable?.form ?? '');
  const [formNote, setFormNote] = useState(receivable?.formNote ?? '');
  const [invalid, setInvalid] = useState('');
  const save = useMutation({
    mutationFn: (fields: Record<string, unknown>) =>
      receivable ? api(`/api/payments/receivables/${receivable.id}`, { method: 'PATCH', body: { version: receivable.version, ...fields } }) : api('/api/payments/receivables', { method: 'POST', body: fields }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: PAYMENTS_KEY });
      onClose();
    },
  });
  const now = { who: who?.id ?? null, event: event?.id ?? null, label, amount, dueDate, form, formNote };
  const opening = useOpening(now);
  const saveNow = useRef<() => Promise<boolean>>(async () => false);
  // owner 2026-10-23 21:14: leaving with unsaved changes asks „Zapisz / Nie zapisuj / Anuluj”
  const guardRef = useUnsavedGuard({ dirty: !sameValues(now, opening), save: () => saveNow.current() });
  if (!me) return null;
  const seeEvents = can(me, 'events.events', 'view');
  const noteLabel = form ? FORM_NOTE_LABEL[form] : undefined;

  /** What to send (on a change only what changed), or null with the reason shown. */
  function toSend(): Record<string, unknown> | null {
    const fail = (why: string) => (setInvalid(why), null);
    setInvalid('');
    if (!who) return fail('Wybierz kontrahenta');
    if (label.trim() === '') return fail('Wpisz opis należności');
    let amountGrosze: number;
    try {
      amountGrosze = parsePln(amount);
    } catch (error) {
      return fail(error instanceof Error ? error.message : 'Nieprawidłowa kwota');
    }
    if (amountGrosze <= 0) return fail('Kwota musi być większa od zera');
    if (!/^\d{4}-\d{2}-\d{2}$/.test(dueDate)) return fail('Podaj termin płatności');
    if (!form) return fail('Wybierz formę płatności');
    const note = noteLabel ? formNote.trim() : '';
    if (form === 'faktura_inna' && note === '') return fail('Podaj, kto wystawia fakturę (Podmiot)');
    const eventId = event?.id ?? null;
    if (!receivable) return { contractorId: who.id, label: label.trim(), amountGrosze, dueDate, form, formNote: note, ...(seeEvents ? { eventId } : {}) };
    return {
      ...(who.id !== receivable.contractorId ? { contractorId: who.id } : {}),
      ...(seeEvents && eventId !== receivable.eventId ? { eventId } : {}),
      ...(label.trim() !== receivable.label ? { label: label.trim() } : {}),
      ...(amountGrosze !== receivable.amountGrosze ? { amountGrosze } : {}),
      ...(dueDate !== receivable.dueDate ? { dueDate } : {}),
      ...(form !== receivable.form ? { form } : {}),
      ...(form !== receivable.form || note !== (receivable.formNote ?? '') ? { formNote: note } : {}),
    };
  }
  function submit(e: FormEvent) {
    e.preventDefault();
    const fields = toSend();
    if (!fields) return;
    if (receivable && Object.keys(fields).length === 0) return onClose();
    save.mutate(fields);
  }
  saveNow.current = async () => {
    const fields = toSend();
    if (!fields) return false;
    if (!receivable || Object.keys(fields).length > 0) await save.mutateAsync(fields);
    return true;
  };
  const error = save.error instanceof ApiError ? save.error : null;
  const title = receivable ? 'Zmiana należności' : 'Nowa należność';
  return (
    <div className="dialog-backdrop">
      <form
        ref={guardRef}
        className="dialog panel form"
        role="dialog"
        aria-modal="true"
        aria-labelledby="manual-receivable-title"
        data-feature-id="payments.manual"
        noValidate
        onSubmit={submit}
        onKeyDown={(e) => {
          if (e.key === 'Tab') trapTab(e.currentTarget, e.nativeEvent);
        }}
      >
        <DialogLayer onClose={onClose} busy={save.isPending} />
        <h2 id="manual-receivable-title">{title}</h2>
        {who ? (
          <p className="rental-item">
            <span className="muted">Kontrahent</span> <strong>{who.name}</strong>{' '}
            <button type="button" className="btn small ghost" onClick={() => setWho(null)}>
              Zmień
            </button>
          </p>
        ) : (
          <QuickPicker<Option> label="Kontrahent" placeholder="Szukaj kontrahenta…" search={searchContractors} onPick={(o) => setWho({ id: o.id, name: o.name })} />
        )}
        <label className="field">
          <span>Opis</span>
          <input type="text" maxLength={200} value={label} placeholder="np. wynajem mikrofonów" onChange={(e) => setLabel(e.target.value)} />
        </label>
        <div className="field-row">
          <label className="field narrow-field">
            <span>Kwota brutto (zł)</span>
            <input
              inputMode="decimal"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              onBlur={() => {
                try {
                  setAmount(plnToInput(parsePln(amount)));
                } catch {
                  /* the message comes on saving */
                }
              }}
            />
          </label>
          <label className="field narrow-field">
            <span>Termin płatności</span>
            <input type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)} />
          </label>
        </div>
        <label className="field">
          <span>Forma płatności</span>
          <select value={form} onChange={(e) => setForm(e.target.value as Form | '')}>
            <option value="">— wybierz —</option>
            {(Object.keys(FORMS) as Form[]).map((f) => (
              <option key={f} value={f}>
                {FORMS[f]}
              </option>
            ))}
          </select>
        </label>
        {noteLabel && (
          <label className="field">
            <span>{noteLabel}</span>
            <input type="text" maxLength={200} value={formNote} onChange={(e) => setFormNote(e.target.value)} />
          </label>
        )}
        {seeEvents &&
          (event ? (
            <p className="rental-item">
              <span className="muted">Wydarzenie</span> <strong>{event.name}</strong>{' '}
              <button type="button" className="btn small ghost" onClick={() => setEvent(null)}>
                Usuń
              </button>
            </p>
          ) : (
            <QuickPicker<Option> label="Wydarzenie (opcjonalnie)" placeholder="Szukaj wydarzenia…" search={searchEvents} onPick={(o) => setEvent({ id: o.id, name: o.name })} />
          ))}
        {invalid && (
          <p className="alert" role="alert">
            {invalid}
          </p>
        )}
        {error?.status === 409 && (
          <ConflictNotice
            onReload={() => {
              void queryClient.invalidateQueries({ queryKey: PAYMENTS_KEY });
              onClose();
            }}
          />
        )}
        {error && error.status !== 409 && (
          <p className="alert" role="alert">
            {fieldIssues(error)[0]?.message ?? error.message}
          </p>
        )}
        {save.error && !error && (
          <p className="alert" role="alert">
            Nie udało się zapisać
          </p>
        )}
        <div className="actions">
          <button className="btn primary" type="submit" disabled={save.isPending}>
            {save.isPending ? 'Zapisywanie…' : receivable ? 'Zapisz' : 'Dodaj'}
          </button>
          <button className="btn" type="button" onClick={onClose}>
            Anuluj
          </button>
        </div>
      </form>
    </div>
  );
}

/** „Usuń” of a manual receivable: asked first; a receivable with payments is refused (the server's reason shown). */
export function DeleteManualDialog({ receivable, onClose }: { receivable: Receivable; onClose: () => void }) {
  const queryClient = useQueryClient();
  const remove = useMutation({
    mutationFn: () => api(`/api/payments/receivables/${receivable.id}?version=${receivable.version}`, { method: 'DELETE' }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: PAYMENTS_KEY });
      onClose();
    },
  });
  const conflict = remove.error instanceof ApiError && remove.error.status === 409;
  return (
    <div className="dialog-backdrop">
      <div className="dialog panel" role="alertdialog" aria-modal="true" aria-labelledby="manual-delete-title" data-feature-id="payments.manual">
        <DialogLayer onClose={onClose} busy={remove.isPending} />
        <h2 id="manual-delete-title">Usunąć należność?</h2>
        <p>
          „{receivable.label}”{receivable.contractorName ? ` (${receivable.contractorName})` : ''} zniknie z otwartych należności i przestanie liczyć się w sumach. W historii zmian zostaje wpis o usunięciu.
        </p>
        <p className="muted small">Jeśli były na nią wpłaty, które potem usunięto, należność zostanie anulowana zamiast usunięta — zostaje w „Wszystkie” razem z zapisem usuniętych wpłat.</p>
        {conflict && (
          <ConflictNotice
            onReload={() => {
              void queryClient.invalidateQueries({ queryKey: PAYMENTS_KEY });
              onClose();
            }}
          />
        )}
        {remove.error && !conflict && (
          <p className="alert" role="alert">
            {remove.error instanceof ApiError ? remove.error.message : 'Nie udało się usunąć'}
          </p>
        )}
        <div className="actions">
          <button className="btn primary" type="button" disabled={remove.isPending} onClick={() => remove.mutate()}>
            {remove.isPending ? 'Usuwanie…' : 'Usuń'}
          </button>
          <button className="btn" type="button" onClick={onClose}>
            Anuluj
          </button>
        </div>
      </div>
    </div>
  );
}
